import React, { useCallback, useEffect, useState } from "react";

import {
  TableCell,
  TextField,
  Typography
} from "@mui/material";

import { EDIT_ROLES } from "./EntitiesTab.types";

// Internal shared-state for detecting concurrent cell edits.
//
// Because each cell creates an `Input` component that registers an `onKeyDown`
// handler, we use a global `keydownTarget` reference so the last-cell that
// received focus is the one that actually processes keyboard events — preventing
// ghost inputs from lingering after React unmounts.
let keydownTarget: HTMLInputElement | null = null;
let previousValue: number | null = null;

/**
 * Inline editor for the allocation percentage column.
 *
 * Shows a **visible red border and helper text** when the value is outside
 * the permitted range (1–100), per PRD §124/§137.
 */
export const AllocationCellEditor: React.FC<
  import("./EntitiesTab.types").IAllocationCellProps
> = ({ initialValue, roleId, onUpdate, onCancel }) => {
  const [value, setValue] = useState<string>(String(initialValue));
  const [isDirty, setIsDirty] = useState(false);

  const isEditable =
    roleId !== null &&
    EDIT_ROLES.includes(roleId);

  // Track the input element for focus management.
  const inputElementRef = React.useRef<HTMLInputElement | null>(null);

  // Focus the input as soon as the cell enters edit mode.
  useEffect(() => {
    if (isEditable && inputElementRef.current) {
      inputElementRef.current.focus();
      inputElementRef.current.select();
    }
  }, [isEditable]);

  // ---- Helpers ----------------------------------------------------------

  // Whether the current typed value is valid (PRD: 1-100).
  const parsedValue = Number(value);
  const isValid =
    !Number.isNaN(parsedValue) && parsedValue >= 1 && parsedValue <= 100;

  const fireUpdate = useCallback(() => {
    // Discard values that cannot be parsed or are out of range, but close the editor.
    if (Number.isNaN(parsedValue) || parsedValue < 1 || parsedValue > 100) {
      onCancel();
      return;
    }
    onUpdate(parsedValue);
  }, [parsedValue, onUpdate, onCancel]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(e.target.value);
    },
    []
  );

  /**
   * Called when the user has typed something and we need to track that
   * the value is no longer the initialValue — this gate keeps the validation
   * border from flashing red before the user has touched the field.
   */
  const handleChangeWithDirty = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(e.target.value);
      setIsDirty(true);
    },
    []
  );

  // ---- Keyboard handler (de-duplicated by keydownTarget) ----------------

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.target !== keydownTarget) return;

      if (e.key === "Enter") {
        e.preventDefault();
        keydownTarget = null;
        previousValue = null;
        fireUpdate();
      }

      if (e.key === "Escape") {
        e.preventDefault();
        keydownTarget = null;
        previousValue = null;
        onCancel();
      }
    },
    [fireUpdate, onCancel]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  // ---- Blur handler -----------------------------------------------------

  const handleBlur = useCallback(() => {
    // Prevent a race when focus moves to other buttons
    setTimeout(() => {
      keydownTarget = null;
      previousValue = null;
      fireUpdate();
    }, 150);
  }, [fireUpdate]);

  // ---- Render -----------------------------------------------------------

  if (!isEditable) {
    return (
      <TableCell align="center" sx={{ height: "80px", p: 0 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          sx={{ width: "100%", height: "100%" }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "#071F35",
              fontWeight: 600,
              backgroundColor: "#b2eaad",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "96px",
              py: "4px",
              borderRadius: "4px"
            }}
          >
            {initialValue}%
          </Typography>
        </Stack>
      </TableCell>
    );
  }

  return (
    <TableCell align="center" sx={{ p: 0, minWidth: 100 }}>
      <TextField
        inputRef={(el: HTMLInputElement | null) => {
          inputElementRef.current = el;
          keydownTarget = el;
          if (el) previousValue = initialValue;
        }}
        inputProps={{
          max: 100,
          min: 1,
          step: 1
        }}
        size="small"
        // Show red border + helper text only after the user has typed (isDirty)
        // AND the value is out of range — per PRD §124.
        error={isDirty && !isValid}
        helperText={
          isDirty && !isValid ? "Enter a number between 1 and 100." : undefined
        }
        sx={{
          width: 120,
          textAlign: "center",
          py: 1,
          "& .MuiOutlinedInput-root": {
            pl: 1,
            py: 0,
            px: 1,
            "&:hover": { bgcolor: "#f5f7f7" }
          }
        }}
        type="number"
        onChange={isValid || !isDirty ? handleChange : handleChangeWithDirty}
        onBlur={handleBlur}
        value={value}
      />
    </TableCell>
  );
};
